import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUsers, setUserType } from '@store/slices/users/usersSlice';
import { CONTRACTOR_USER, MOBILE_USER, WEB_USER } from '@routes/helpers';

export const OPTIONS = [
  { value: WEB_USER, label: 'Rideshare' },
  { value: CONTRACTOR_USER, label: 'Contractor Portal' },
  { value: 'memberPortal', label: 'Member Portal' },
  { value: 'payerPortal', label: 'Payer Portal' },
  { value: MOBILE_USER, label: 'Mobile User' },
] as const;

export type UserType = (typeof OPTIONS)[number]['value'];

export const STORAGE_VERSION_KEY = 'Version';

const VersionSelect = () => {
  const dispatch = useDispatch();
  const { userType } = useSelector(selectUsers);

  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(
    Math.max(
      0,
      OPTIONS.findIndex((o) => o.value === userType)
    )
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = OPTIONS.find((o) => o.value === userType);

  const choose = (value: UserType) => {
    dispatch(setUserType(value));
    localStorage.setItem(STORAGE_VERSION_KEY, value);
    setOpen(false);
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      !open &&
      (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')
    ) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % OPTIONS.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((i) => (i - 1 + OPTIONS.length) % OPTIONS.length);
        break;
      case 'Enter':
        e.preventDefault();
        choose(OPTIONS[highlightIndex].value);
        break;
      case 'Escape':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      onKeyDown={handleKeyDown}
      style={{ position: 'relative', width: 260, fontFamily: 'sans-serif' }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '10px 12px',
          borderRadius: 8,
          border: '1px solid #d1d5db',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: selectedOption ? '#111827' : '#9ca3af' }}>
          {selectedOption ? selectedOption.label : 'Select a version...'}
        </span>
        <span style={{ marginLeft: 8, opacity: 0.6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            margin: 0,
            padding: 4,
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 10,
          }}
        >
          {OPTIONS.map((opt, i) => {
            const isSelected = opt.value === userType;
            const isHighlighted = i === highlightIndex;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHighlightIndex(i)}
                onClick={() => choose(opt.value)}
                style={{
                  padding: '8px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: isHighlighted ? '#f3f4f6' : 'transparent',
                  fontWeight: isSelected ? 600 : 400,
                  color: '#111827',
                }}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ConfigWrapper: FC = () => {
  return (
    <div>
      <VersionSelect />
      <Outlet />
    </div>
  );
};

export default ConfigWrapper;
